<script>
import ButtonNext from 'next/button/Button.vue';
import IconNext from 'next/icon/Icon.vue';
import getUuid from 'widget/helpers/uuid';
import ConditionField from '../conditionField/Index.vue';
import DynamicField from '../dynamicField/Index.vue';

export default {
  components: {
    DynamicField,
    ConditionField,
    IconNext,
    ButtonNext,
  },
  props: {
    branch: {
      type: Object,
      default: () => {},
    },
    index: {
      type: Number,
      required: true,
    },
    variables: {
      type: Array,
      default: () => [],
    },
    disableRemove: {
      type: Boolean,
      default: false,
    },
    isFirst: {
      type: Boolean,
      default: false,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['branchChange', 'moveUp', 'moveDown', 'remove'],
  data() {
    return {
      fallbackName: this.$t(
        'WGPT_AUTOMATIONS.EDITOR.BRANCH.FALLBACK_BRANCH_NAME',
        {
          index: this.index + 1,
        }
      ),
      updatedName: this.branch.name,
      isEditing: false,
    };
  },
  computed: {
    branchName() {
      return this.branch.name || this.fallbackName;
    },
    branchRegistry() {
      return this.$store.getters['wgptAutomationRegistry/getAction']('branch');
    },
    branchFields() {
      return this.branchRegistry.branch_fields || [];
    },
    conditions() {
      return this.branch.conditions || [];
    },
    disabledRemoveCondition() {
      if (this.disabled) return true;
      return this.conditions.length <= 1;
    },
    logicalOperators() {
      return this.$store.getters['wgptAutomationRegistry/getLogicalOperators'];
    },
    logicalOperatorField() {
      return {
        name: 'logical_operator',
        type: 'select',
        options: this.logicalOperators,
        required: true,
      };
    },
    enabled() {
      return !this.disabled;
    },
  },
  watch: {
    branch: {
      handler(branch) {
        if (branch.name !== this.updatedName) {
          this.updatedName = branch.name || `Branch ${this.index + 1}`;
        }
      },
      immediate: true,
    },
  },
  methods: {
    onBranchChange(fieldName, value) {
      const branchUpdates = {
        [fieldName]: value,
      };
      this.$emit('branchChange', branchUpdates);
    },
    onConditionChange(conditionIndex, value) {
      const conditionsBefore = this.conditions;
      const conditions = conditionsBefore.map((condition, index) => {
        if (index === conditionIndex) {
          return {
            ...condition,
            ...value,
          };
        }
        return condition;
      });
      const branchUpdates = {
        conditions,
      };
      this.$emit('branchChange', branchUpdates);
    },
    addCondition() {
      const condition = {
        variable_name: '',
        operator: 'equals',
        value: '',
      };

      const conditionsBefore = this.conditions;
      const conditions = conditionsBefore.concat(condition);
      const branchUpdates = {
        conditions,
      };
      this.$emit('branchChange', branchUpdates);
    },
    removeCondition(conditionIndex) {
      const conditionsBefore = this.conditions;
      const conditions = conditionsBefore.filter(
        (condition, index) => index !== conditionIndex
      );
      const branchUpdates = {
        conditions,
      };
      this.$emit('branchChange', branchUpdates);
    },
    editName() {
      this.isEditing = true;
      this.$nextTick(() => {
        this.$refs.branchNameInput.focus();
      });
    },
    onNameChange() {
      this.onBranchChange('name', this.updatedName);
      this.isEditing = false;
      this.$refs.branchNameInput.blur();
    },
    cancelNameEditing() {
      this.isEditing = false;
    },
    getUuid() {
      return getUuid();
    },
  },
};
</script>

<template>
  <div class="rounded-xl bg-n-solid-2">
    <header
      class="group p-2 flex items-center gap-2"
      :class="{ 'cursor-grab': enabled }"
    >
      <IconNext
        icon="i-lucide-grip-vertical"
        class="text-n-slate-11 size-3.5 shrink-0"
      />
      <input
        v-show="isEditing"
        ref="branchNameInput"
        v-model.trim="updatedName"
        :placeholder="fallbackName"
        class="!text-sm ignore-drag !ps-1 !bg-transparent disabled:!bg-transparent disabled:!cursor-text !text-slate-900 dark:!text-slate-100 !border-none !outline !outline-2 !outline-transparent -outline-offset-2 !m-0 !font-semibold !h-8"
        dir="auto"
        type="text"
        @blur="onNameChange"
        @keyup.enter="onNameChange"
        @keyup.esc="cancelNameEditing"
      />
      <h5
        v-if="!isEditing"
        class="px-1 grow text-sm font-semibold text-black-900 dark:text-slate-200 select-none inline-flex items-center gap-1.5 overflow-hidden"
      >
        <span class="truncate">{{ branchName }}</span>
        <ButtonNext
          v-if="enabled"
          color="slate"
          size="xs"
          variant="ghost"
          icon="i-lucide-pencil"
          class="invisible group-hover:visible ignore-drag"
          @click="editName"
        />
      </h5>
      <aside class="ms-auto shrink-0 flex gap-1">
        <template v-if="!isEditing">
          <ButtonNext
            size="sm"
            icon="i-lucide-move-up"
            variant="ghost"
            :disabled="disabled || isFirst"
            color="slate"
            class="ignore-drag"
            @click="$emit('moveUp')"
          />
          <ButtonNext
            size="sm"
            icon="i-lucide-move-down"
            variant="ghost"
            :disabled="disabled || isLast"
            color="slate"
            class="ignore-drag"
            @click="$emit('moveDown')"
          />
        </template>
        <ButtonNext
          size="sm"
          icon="i-lucide-x"
          variant="ghost"
          :disabled="disableRemove"
          color="slate"
          class="ignore-drag"
          @click="$emit('remove')"
        />
      </aside>
    </header>

    <div class="border-t border-n-slate-4" />

    <div class="ignore-drag ps-3 pe-1 py-3 flex flex-col gap-4">
      <p class="text-sm text-n-slate-11">
        {{ $t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.BRANCH.CONDITIONS.TITLE') }}
      </p>

      <div class="flex flex-col gap-6">
        <div
          v-for="(condition, conditionIndex) of conditions"
          :key="`${conditionIndex}-${getUuid()}`"
          class="condition-field"
        >
          <ConditionField
            :value="condition"
            :variables="variables"
            :disable-remove="disabledRemoveCondition"
            :disabled="disabled"
            :dirty="dirty"
            @change="value => onConditionChange(conditionIndex, value)"
            @remove="removeCondition(conditionIndex)"
          >
            <template #operator-field>
              <DynamicField
                class="operator-field"
                :field="logicalOperatorField"
                :value="branch[logicalOperatorField.name]"
                :visible="conditions.length > 1"
                :disabled="disabled || conditionIndex > 0"
                :dirty="dirty"
                @change="e => onBranchChange(logicalOperatorField.name, e)"
              />
            </template>
          </ConditionField>
        </div>
      </div>

      <div v-if="enabled">
        <ButtonNext
          color-scheme="primary"
          size="sm"
          icon="i-lucide-plus"
          @click="addCondition"
        >
          {{
            $t(
              'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.BRANCH.ADD_CONDITION_BUTTON_TEXT'
            )
          }}
        </ButtonNext>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.condition-field:last-child .operator-field {
  @apply hidden;
}
</style>
